import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

// models
import { KalturaConfig } from 'src/config/config.model';

// services
import { AuthService } from './auth/auth.service';
import { UserProfile } from './yeti-protocol/auth/mi';

export interface AOVideosServiceConfig {
  context: string;
  kaltura: KalturaConfig
}

export interface ScriptModel {
  name: string,
  src: string,
  loaded: boolean
}

export const AO_VIDEOS_SERVICE_CONFIG = new InjectionToken<AOVideosServiceConfig>('ao-videos.service.config');

@Injectable()
export class AOVideosService implements OnDestroy {

  private scriptUrl: string;
  private script: ScriptModel;
  private kWidget;
  private user: UserProfile;
  private userSubscription: Subscription;

  constructor(
    @Inject(AO_VIDEOS_SERVICE_CONFIG) private config: AOVideosServiceConfig,
    private authService: AuthService,
  ) {
    this.init();
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
  }

  async getContentForKalturaVideo(
    articleId: string,
    widgetId: string
  ): Promise<string> {

    const kalturaSdkLoaded = await this.loadScript();

    if (!kalturaSdkLoaded) {
      return '';
    }

    if (!this.kWidget || !this.user?.aoId) {
      return '';
    }

    return [
      '<div id="video" runat="server" visible="false">',
      `<script type="text/javascript" src="${this.scriptUrl}"></script>`,
      /* eslint-disable */
      '<div runat="server" id="kaltura_player_1" style="position: absolute; top: 0; left: 0; left: 0; right: 0; bottom: 0px;"></div>',
      /* eslint-enable */
      '<script type="text/javascript">',
      'kWidget.embed("kaltura_player_1",{',
      `"wid": "${widgetId}",`,
      `"uiconf_id":${this.config.kaltura.uiconfId},`,
      `"entry_id": "${articleId}",`,
      '"flashvars": {',
      `"userId": "${this.user?.aoId}",`,
      '"statistics": {',
      '"plugin": true,',
      '"applicationName": "MyAO",',
      '"hideKs": true',
      '},',
      '"streamerType": "auto",',
      '"localizationCode" : "en",',
      '"leadWithHTML5" : "true",',
      '"sideBarContainer.plugin" : "true",',
      '"sideBarContainer.position" : "left",',
      '"sideBarContainer.clickToClose" : "true",',
      '"chapters.plugin" : "true",',
      '"chapters.layout" : "vertical",',
      '"chapters.thumbnailRotator" : "false",',
      '"streamSelector.plugin" : "true",',
      '"EmbedPlayer.SpinnerTarget" : "videoHolder",',
      '"dualScreen.plugin" : "true",',
      '"expandToggleBtn.plugin" : "false",',
      '"EmbedPlayer.EnableFullscreen" : "true",',
      '"moderation.plugin" : "false"',
      '}',
      '});',
      '</script>',
      '</div>'
    ].join('\n');
  }

  private init(): void {
    this.initKalturaSDKUrl();
    this.initScriptModel();
    this.loadScript();
    this.subscribeToUserProfileChanges();
  }

  private initKalturaSDKUrl(): void {
    /* eslint-disable */
    this.scriptUrl = `https://cdnapisec.kaltura.com/p/${this.config.kaltura.partnerId}/sp/${this.config.kaltura.partnerId}00/embedIframeJs/uiconf_id/${this.config.kaltura.uiconfId}/partner_id/${this.config.kaltura.partnerId}`;
    /* eslint-enable */
  }

  private initScriptModel(): void {
    this.script = {
      name: this.config.context,
      src: this.scriptUrl,
      loaded: false
    }
  }

  private loadScript(): Promise<boolean> {

    if (this.kWidget) {
      return Promise.resolve(true);
    }

    const scriptElement = document.createElement('script');
    scriptElement.type = 'text/javascript';
    scriptElement.src = this.script.src;

    return new Promise((resolve, reject) => {
      scriptElement.onload = () => {
        this.script.loaded = true;
        this.kWidget = (window as any)?.kWidget;
        resolve(true);
      };

      scriptElement.onerror = (err) => {
        console.error(`
        Couldn't load script: ${this.script.src}
        Error: ${err}
        `);
        reject(false);
      };

      document.getElementsByTagName('body')[0].appendChild(scriptElement);
    });
  }

  private subscribeToUserProfileChanges(): void {
    this.userSubscription = this.authService.userProfileAsObservable.subscribe(userProfile => {
      this.user = userProfile;
    });
  }
}
